import { AuthSessionStore } from "../api/auth_session.js";
import { PersistentCache } from "./persistent_cache.js";

export type OfflineActionType =
  | "level_start"
  | "level_complete"
  | "level_failed"
  | "level_double_reward"
  | "purchase"
  | "booster_consume"
  | "booster_purchase"
  | "reward_heart"
  | "reward_booster"
  | "booster_tutorial";

export type OfflineActionState = "pending" | "inflight";

export interface OfflineAction
{
  id: string;
  type: OfflineActionType;
  payload: Record<string, unknown>;
  created_at: number;
  state: OfflineActionState;
  player_id?: number | null;
}

const STORAGE_KEY = "journal_v1";
const MAX_ACTIONS = 5000;

const AT_MOST_ONCE_TYPES: ReadonlySet<OfflineActionType> = new Set<OfflineActionType>( [
  "booster_consume",
  "booster_purchase",
  "purchase",
] );

export function isAtMostOnceAction( type: OfflineActionType ): boolean
{
  return AT_MOST_ONCE_TYPES.has( type );
}

function newActionId(): string
{
  return globalThis.crypto?.randomUUID?.()
    ?? `act_${Date.now()}_${Math.random().toString( 36 ).slice( 2 )}`;
}

type JournalListener = ( pending_count: number ) => void;

export class OfflineJournal
{
  private static actions: OfflineAction[] | null = null;
  private static listeners = new Set<JournalListener>();

  public static append( type: OfflineActionType, payload: Record<string, unknown> = {} ): OfflineAction
  {
    const list = this.load();
    const action: OfflineAction = {
      id: newActionId(),
      type,
      payload,
      created_at: Date.now(),
      state: "pending",
      player_id: AuthSessionStore.getPlayerId(),
    };
    list.push( action );
    while ( list.length > MAX_ACTIONS )
      list.shift();
    this.persist();
    console.info( `[OfflineJournal] +${type}`, payload, `(${list.length} queued)` );
    this.notify();
    return action;
  }

  public static list(): readonly OfflineAction[]
  {
    return this.load();
  }

  public static count(): number
  {
    return this.load().length;
  }

  public static hasPending(): boolean
  {
    return this.load().length > 0;
  }

  public static markInflight( id: string ): void
  {
    const action = this.load().find( a => a.id === id );
    if ( !action ) return;
    action.state = "inflight";
    this.persist();
  }

  public static patchPayload( id: string, patch: Record<string, unknown> ): void
  {
    const action = this.load().find( a => a.id === id );
    if ( !action ) return;
    action.payload = { ...action.payload, ...patch };
    this.persist();
  }

  public static markPending( id: string ): void
  {
    const action = this.load().find( a => a.id === id );
    if ( !action ) return;
    action.state = "pending";
    this.persist();
  }

  public static remove( id: string ): void
  {
    const list = this.load();
    const index = list.findIndex( a => a.id === id );
    if ( index < 0 ) return;
    list.splice( index, 1 );
    this.persist();
    this.notify();
  }

  public static clear(): void
  {
    this.actions = [];
    this.persist();
    this.notify();
  }

  public static subscribe( listener: JournalListener ): () => void
  {
    this.listeners.add( listener );
    return () => this.listeners.delete( listener );
  }

  private static notify(): void
  {
    const count = this.load().length;
    for ( const listener of this.listeners )
    {
      try { listener( count ); } catch ( e ) { console.warn( "[OfflineJournal] listener failed", e ); }
    }
  }

  private static load(): OfflineAction[]
  {
    if ( this.actions ) return this.actions;
    const stored = PersistentCache.read<unknown>( STORAGE_KEY );
    this.actions = Array.isArray( stored )
      ? stored.filter( ( a ): a is OfflineAction =>
          !!a && typeof a === "object"
          && typeof ( a as OfflineAction ).id === "string"
          && typeof ( a as OfflineAction ).type === "string"
          && !!( a as OfflineAction ).payload )
      : [];
    return this.actions;
  }

  private static persist(): void
  {
    PersistentCache.write( STORAGE_KEY, this.actions ?? [] );
  }
}
